<template>
  <div>
    <trek-loading :top="loading" :page="downloading" :is-loading="loading || downloading">
      <div class="section-header section-header--xl section-header--dividing mt-3">
        <h1 class="heading">{{ bp('sellSheet') }}</h1>
      </div>

      <p class="md:w-1/2">{{ bp('selectFeatures') }}</p>

      <div class="section-header section-header--md section-header--dividing mt-4">
        <h3 class="heading">{{ bp('header') }}</h3>
      </div>

      <label class="checkbox font-medium">
        <input
          id="header-checkbox-1"
          v-model="formData.showHeader"
          type="checkbox"
          name="showHeader"
          qaid="sellSheetIncludeHeader"
        />
        <span class="control-indicator"></span>
        {{ bp('includeHeader') }}
      </label>

      <!-- Header Options -->
      <fieldset v-if="formData.showHeader" class="form-fieldset lg:ml-5 mt-3">
        <label class="checkbox">
          <input
            id="logo-checkbox-2"
            v-model="formData.showLogo"
            name="showLogo"
            type="checkbox"
            qaid="sellSheetIncludeLogo"
            checked
            @change="setLogoOption"
          />
          <span class="control-indicator"></span>
          {{ bp('includeLogo') }}
        </label>

        <div v-if="formData.showLogo" class="lg:ml-5 mt-3">
          <label for="logo-selection-option-1" class="radio">
            <input
              id="logo-selection-option-1"
              v-model="formData.logo"
              name="logo"
              type="radio"
              value="logo-trek"
              qaid="sellSheetIncludeTrekLogo"
              required
            />
            <span class="control-indicator"></span>
            {{ bp('trek') }}
          </label>

          <label for="logo-selection-option-2" class="radio">
            <input
              id="logo-selection-option-2"
              v-model="formData.logo"
              name="logo"
              type="radio"
              value="logo-brand"
              qaid="sellSheetIncludeElectraLogo"
              required
            />
            <span class="control-indicator"></span>
            {{ bp('electra') }}
          </label>

          <label for="logo-selection-option-3" class="radio">
            <input
              id="logo-selection-option-3"
              v-model="formData.logo"
              type="radio"
              name="logo"
              value="logo-store"
              qaid="sellSheetIncludeStoreLogo"
              required
              checked
            />
            <span class="control-indicator"></span>
            {{ bp('store') }}
          </label>
        </div>

        <!-- POS Data -->

        <div v-if="hasPosData" class="form-fieldset mt-4 lg:max-w-sm text-base">
          <label class="checkbox">
            <input
              id="storeName-checkbox-3"
              v-model="formData.showStoreName"
              type="checkbox"
              name="showStoreName"
              value="true"
              qaid="sellSheetIncludeStoreName"
              checked
            />
            <span class="control-indicator"></span>
            {{ bp('storeName') }}
          </label>

          <div class="ml-5" v-html="displayName"></div>

          <!-- POS Address -->
          <div v-if="address">
            <label class="checkbox mt-4">
              <input
                id="storeAddress-checkbox-4"
                v-model="formData.showStoreAddress"
                type="checkbox"
                name="showStoreAddress"
                value="true"
                qaid="sellSheetIncludeStoreAddress"
                checked
              />
              <span class="control-indicator"></span>
              {{ bp('address') }}
            </label>
            <div class="ml-5">
              {{ address.line1 }}
              <br />
              <span v-if="address.line2">
                <span>{{ address.line2 }}</span>
                <br />
              </span>
              {{ address.regionName }},&nbsp;{{ address.town }}
              <br />
              {{ address.postalCode }}&nbsp;{{ address.displayCountry }}
              <br />
            </div>

            <label class="checkbox mt-4">
              <input
                id="email-checkbox-5"
                v-model="formData.showEmail"
                type="checkbox"
                name="showEmail"
                value="true"
                qaid="sellSheetIncludeEmail"
                checked
              />
              <span class="control-indicator"></span>
              {{ bp('email') }}
            </label>
            <div class="ml-5">{{ address.email }}</div>

            <label class="checkbox mt-4">
              <input
                id="websiteUrl-checkbox-6"
                v-model="formData.showWebsiteUrl"
                type="checkbox"
                name="showWebsiteUrl"
                value="true"
                qaid="sellSheetIncludeWebsite"
                checked
              />
              <span class="control-indicator"></span>
              {{ bp('websiteURL') }}
            </label>
            <div class="ml-5">{{ address.url }}</div>
          </div>

          <!-- Store hours -->
          <div v-if="openingHours">
            <label class="checkbox mt-4">
              <input
                id="storeHours-checkbox-7"
                v-model="formData.showStoreHours"
                type="checkbox"
                name="showStoreHours"
                value="true"
                qaid="sellSheetIncludeStoreHours"
                checked
              />
              <span class="control-indicator"></span>
              {{ bp('hours') }}
            </label>

            <ol class="lg:ml-5 text-base">
              <li v-for="schedule in openingHours" :key="schedule.weekDayLong" class="flex justify-between">
                <span>{{ schedule.weekDayLong }}</span>
                <span>{{ schedule.storeOpeningTime }} - {{ schedule.storeClosingTime }}</span>
              </li>
            </ol>
          </div>
        </div>

        <div class="form-fieldset lg:max-w-sm mt-3">
          <label class="checkbox">
            <input
              id="additionalContactInfo-checkbox-8"
              v-model="formData.showAdditionalContactInfo"
              type="checkbox"
              name="showAdditionalContactInfo"
              value="true"
              qaid="sellSheetIncludeAdditionalContactInfo"
            />
            <span class="control-indicator"></span>
            {{ bp('additionalContactInfo') }}
          </label>
          <form-group
            id="showAdditionalContactInfo-textarea-1"
            v-model="formData.additionalContactInformation"
            v-validate="validations.additionalContactInformation"
            class="lg:ml-5"
            data-vv-as="bp(additionalContactInfo)"
            label="bp(additionalContactInfo)"
            name="additionalContactInformation"
            type="textarea"
            qaid="sellSheetIncludeAdditionalContactInfoInput"
            :disabled="!formData.showAdditionalContactInfo"
            :error="errors.first('additionalContactInformation')"
            :rows="5"
            :show-label="false"
          />
        </div>
      </fieldset>

      <div class="section-header section-header--md section-header--dividing mt-4">
        <h3 class="heading">{{ bp('productInformation') }}</h3>
      </div>

      <div class="lg:max-w-sm">
        <div class="is-required" :class="{'has-error': !!errors.first('color')}">
          <label for="color" class="form-label">{{ bp('colour') }}</label>

          <div class="select">
            <select
              id="color"
              ref="color"
              v-model="formData.color"
              v-validate="'required'"
              name="color"
              class="form-control"
              qaid="sellSheetProductColor"
              @change="resetSize"
            >
              <option v-for="variant in productVariants" :key="variant.id" :value="variant.id">{{
                variant.name
              }}</option>
            </select>
          </div>
          <div v-show="errors.first('color')" class="form-feedback is-invalid">{{ errors.first('color') }}</div>
        </div>
        <div v-if="sizeOptions.length" class="is-required" :class="{'has-error': !!errors.first('size')}">
          <label for="size" class="form-label mt-2">{{ bp('size') }}</label>

          <div class="select">
            <select
              id="size"
              ref="size"
              v-model="formData.size"
              v-validate="'required'"
              name="size"
              class="form-control"
              qaid="sellSheetProductSize"
              @change="onSizeSelect"
            >
              <option v-for="item in sizeOptions" :key="item.id" :data-id="item.id" :value="item.colorSwatchName">{{
                item.size
              }}</option>
            </select>
          </div>
          <div v-show="errors.first('size')" class="form-feedback is-invalid">{{ errors.first('size') }}</div>
        </div>
      </div>

      <fieldset class="form-fieldset lg:max-w-sm mt-3">
        <label class="checkbox">
          <input
            id="includeImage-checkbox-1"
            v-model="formData.showProductImage"
            type="checkbox"
            name="showProductImage"
            qaid="sellSheetProductIncludeImage"
            checked
          />
          <span class="control-indicator"></span>
          {{ bp('includeImage') }}
        </label>

        <div class="mt-4">
          <label class="checkbox">
            <input
              id="additionalProductInfo-checkbox-2"
              v-model="formData.showAdditionalProductInfo"
              type="checkbox"
              name="showAdditionalProductInfo"
              value="true"
              qaid="sellSheetProductAdditionalInfo"
            />
            <span class="control-indicator"></span>
            {{ bp('additionalProductInfo') }}
          </label>
          <form-group
            id="showAdditionalProductInfo-textarea-1"
            v-model="formData.additionalProductInformation"
            v-validate="validations.additionalProductInformation"
            class="lg:ml-5"
            name="additionalProductInformation"
            type="textarea"
            qaid="sellSheetProductAdditionalInfoInput"
            :data-vv-as="bp('additionalProductInfo')"
            :disabled="!formData.showAdditionalProductInfo"
            :label="bp('additionalProductInfo')"
            :error="errors.first('additionalProductInformation')"
            :rows="5"
            :show-label="false"
          />
        </div>
      </fieldset>

      <div class="section-header section-header--md section-header--dividing mt-4">
        <h3 class="heading">{{ bp('pricing') }}</h3>
      </div>

      <fieldset class="form-fieldset lg:max-w-sm">
        <label v-show="pricingInfo.calculableAdvertised" class="checkbox">
          <input
            id="advertisedPrice-checkbox-1"
            v-model="formData.showAdvertisedPrice"
            type="checkbox"
            name="showAdvertisedPrice"
            value="true"
            qaid="sellSheetPricingAdvertised"
          />
          <span class="control-indicator"></span>
          {{ bp('advertised') }}
        </label>
        <label v-show="pricingInfo.closeoutPrice" class="checkbox">
          <input
            id="closeOutPrice-checkbox-2"
            v-model="formData.showCloseOutPrice"
            type="checkbox"
            name="showCloseOutPrice"
            value="true"
            qaid="sellSheetPricingCloseout"
          />
          <span class="control-indicator"></span>
          {{ bp('closeout') }}
        </label>
        <label v-show="pricingInfo.calculableSaleAdvertised" class="checkbox">
          <input
            id="reducedPrice-checkbox-3"
            v-model="formData.showReducedPrice"
            type="checkbox"
            name="showReducedPrice"
            value="true"
            qaid="sellSheetPricingSale"
          />
          <span class="control-indicator"></span>
          {{ bp('salePrice') }}
        </label>
        <label v-show="pricingInfo.applePrice" class="checkbox">
          <input
            id="applePrice-checkbox-4"
            v-model="formData.showApplePrice"
            type="checkbox"
            name="showApplePrice"
            value="true"
            qaid="sellSheetPricingApple"
          />
          <span class="control-indicator"></span>
          {{ bp('applePrice') }}
        </label>

        <label class="checkbox">
          <input
            id="ourPrice-checkbox-5"
            v-model="formData.showOurPrice"
            type="checkbox"
            name="showOurPrice"
            qaid="sellSheetPricingOurPrice"
          />
          <span class="control-indicator"></span>
          {{ bp('ourPrice') }}
        </label>
        <form-group
          id="ourPrice-text-1"
          v-model="formData.ourPrice"
          v-validate="validations.ourPrice"
          class="ml-5"
          name="ourPrice"
          type="text"
          qaid="sellSheetPricingOurPriceInput"
          :data-vv-as="bp('ourPrice')"
          :disabled="!formData.showOurPrice"
          :error="errors.first('ourPrice')"
          :label="bp('ourPrice')"
          :show-label="false"
        />
      </fieldset>

      <div class="buttons pb-3">
        <trek-button
          id="downloadSellSheetButton"
          ref="downloadButton"
          primary
          icon="get_app"
          qaid="sellSheetDownload"
          @click.stop="onSubmit"
        >
          <span v-html="bp('download')"></span>
        </trek-button>
        <trek-link button secondary :href="returnUrl" qaid="sellSheetCancel">
          <span v-html="bp('cancel')"></span>
        </trek-link>
      </div>
    </trek-loading>
  </div>
</template>

<script>
import axios from 'axios';
import keyBy from 'lodash/keyBy';
import sortBy from 'lodash/sortBy';
import {mapState} from 'vuex';
import {downloadFile} from '@/utils/file-download';
import TrekButton from '@/components/TrekButton';
import TrekLink from '@/components/TrekLink';

export default {
  components: {TrekButton, TrekLink},
  props: {
    address: {
      type: Object,
      default: () => {
        return {
          line1: null,
          line2: null,
          regionName: null,
          town: null,
          postalCode: null,
          displayCountry: null,
          email: null,
          url: null,
        };
      },
    },
    baseProductId: {
      type: String,
      required: true,
    },
    displayName: {
      type: String,
      default: undefined,
    },
    hasPosData: {
      type: Boolean,
      required: true,
    },
    isAppleLabelDealer: {
      type: Boolean,
      required: true,
    },
    openingHours: {
      type: Array,
      default: () => [],
    },
    productCode: {
      type: String,
      required: true,
    },
    returnUrl: {
      type: String,
      required: true,
    },
    sellSheetPdfUrl: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      includeImage: false,
      productVariants: null,
      loading: true,
      downloading: false,
      pricingInfo: {
        applePrice: null,
        bulkPrices: null,
        bulkSalePrices: null,
        calculableAdvertised: null,
        calculableSaleAdvertised: null,
        closeoutPrice: null,
        price: null,
        savedAmount: null,
        wasPrice: null,
      },
      formData: {
        additionalContactInformation: '',
        additionalProductInformation: '',
        color: null,
        logo: 'logo-store',
        ourPrice: '',
        pdfName: '',
        pdfView: true,
        productCode: this.productCode,
        showAdditionalContactInfo: false,
        showAdditionalProductInfo: false,
        showAdvertisedPrice: !this.isAppleLabelDealer,
        showApplePrice: this.isAppleLabelDealer,
        showCloseOutPrice: false,
        showEmail: true,
        showHeader: false,
        showLogo: true,
        showOurPrice: false,
        showProductImage: true,
        showReducedPrice: false,
        showStoreAddress: true,
        showStoreHours: true,
        showStoreName: true,
        showWebsiteUrl: true,
        size: null,
        suffixTimestamp: false,
        tidyHTML: true,
      },
    };
  },

  computed: {
    ...mapState('backend', ['encodedContextPath']),

    // Object of validations where the property matches a given form data property so they can be
    // referenced using the same name
    validations() {
      return {
        ourPrice: this.formData.showOurPrice ? 'required' : null,
        additionalProductInformation: this.formData.showAdditionalProductInfo ? 'required' : null,
        additionalContactInformation: this.formData.showAdditionalContactInfo ? 'required' : null,
      };
    },

    // Map the product variants (retrieved via API call) by color.
    variantsByColor() {
      if (!this.productVariants) return [];
      return keyBy(this.productVariants, 'id');
    },

    // Return only the size options available for the currently selected color
    sizeOptions() {
      if (!this.variantsByColor || !this.formData.color) return [];
      const sizes = this.variantsByColor[this.formData.color].sizes;
      return sortBy(sizes, ['id']);
    },

    pdfName() {
      return `Sell_Sheet_${this.formData.productCode}`;
    },

    // Split the bound form data values into a query string to be sent with the request for the PDF
    query() {
      const esc = encodeURIComponent;

      return Object.keys(this.formData)
        .map((k) => esc(k) + '=' + esc(this.formData[k]))
        .join('&');
    },

    // The URL that will get used as the target for getting the PDF
    requestUrl() {
      return `${this.sellSheetPdfUrl}${this.formData.productCode}/pdf?${this.query}`;
    },
  },

  mounted() {
    this.formData.pdfName = this.pdfName;

    // Do the API call for pricing info and product variants so we can
    // set other required local scope data
    axios.all([this.getPrices(), this.getOtherPrices(), this.getVariants()]).then(
      axios.spread((pricing, otherPrices, variants) => {
        this.setApplePrice(pricing.data);
        this.setOtherPrices(otherPrices.data);
        this.productVariants = variants.data.data;
        this.setSize();
        this.setColor();
        this.loading = false;
      })
    );
  },

  methods: {
    getPrices() {
      return axios.get(`${this.encodedContextPath}/p/pricinginfo/${this.formData.productCode}/`);
    },

    getOtherPrices() {
      return axios.get(`${this.encodedContextPath}/p/${this.formData.productCode}/price`);
    },

    setApplePrice({applePrice}) {
      if (this.isAppleLabelDealer) {
        this.pricingInfo.applePrice = applePrice;
      }
    },

    setOtherPrices({formattedValue, formattedCloseoutWholesalePrice, calculableSaleAdvertised}) {
      this.pricingInfo.calculableAdvertised = formattedValue;
      this.pricingInfo.closeoutPrice = formattedCloseoutWholesalePrice;
      this.pricingInfo.calculableSaleAdvertised = calculableSaleAdvertised;
    },

    onSizeSelect(e) {
      this.formData.productCode = e.target.options[e.target.options.selectedIndex].getAttribute('data-id');
      this.setSize();
      this.updatePricesOnSizeSelect();
    },

    updatePricesOnSizeSelect() {
      this.getPrices().then((response) => {
        this.setApplePrice(response.data);

        this.getOtherPrices().then((response) => {
          this.setOtherPrices(response.data);
        });
      });
    },

    getVariants() {
      return axios.get(`/technical-specification/selector/matrix/${this.baseProductId}`);
    },

    downloadPdf() {
      this.downloading = true;
      downloadFile(this.requestUrl, `${this.pdfName}.pdf`, 'application/pdf')
        .then(() => {
          this.$nextTick(() => {
            this.downloading = false;
            this.redirectToPdp();
          });
        })
        .catch((e) => {
          this.downloading = false;
          this.$notify({
            type: 'warning',
            message: e.message,
          });
        });
    },

    onSubmit(e) {
      // Stop the initial event immediately if the size isn't set. This must be done for IE11
      // because the validation function is a promise and won't prevent in time for IE.
      if (!this.formData.size) {
        e.preventDefault();
      }

      // Validate using v-validate. If there are errors, scroll the browser to top-most error
      // on the page
      this.$validator.validate().then((valid) => {
        if (!valid) {
          e.preventDefault();
          const firstErrorFieldName = Object.keys(this.$validator.errors.collect())[0];
          const firstErrorFieldEl = document.querySelector(`[name="${firstErrorFieldName}"]`);
          this.$scrollTo(firstErrorFieldEl);
        } else {
          return this.downloadPdf();
        }
      });
    },

    setLogoOption(e) {
      if (e.target.value && this.logo == null) return (this.logo = 'logo-trek');
      return (this.logo = null);
    },

    setSize() {
      return this.productVariants.findIndex((variant) => {
        if (!variant.sizes || !variant.sizes.length) return false;

        let sizeIndex = variant.sizes.findIndex((size) => size.id == this.formData.productCode);
        if (sizeIndex === -1) {
          sizeIndex = 0;
        }
        this.formData.size = variant.sizes[sizeIndex].colorSwatchName;

        return sizeIndex;
      });
    },

    setColor() {
      const sizeIndex = this.setSize();
      const variantIndex = sizeIndex > 0 ? sizeIndex : 0;
      return (this.formData.color = this.productVariants[variantIndex].id);
    },

    redirectToPdp() {
      return location.replace(this.returnUrl);
    },

    /**
     * Reset size options to the first one when color is changed
     */
    resetSize() {
      if (this.sizeOptions.length) {
        this.formData.size = this.sizeOptions[0].colorSwatchName;
        this.formData.productCode = this.sizeOptions[0].id;
      } else {
        this.formData.size = null;
      }

      this.updatePricesOnSizeSelect();
    },
  },
};
</script>
